/**
 * AP Invoices Demo — "make me an AP processing workflow" canvas.
 *
 * THROWAWAY DEMO CODE. Renders the AP processing pipeline as a ReactFlow graph
 * using the PRODUCT'S real `StepNode`, so it reads as the actual workflow
 * editor. Nami's chat tools flip the `apWorkflow` build flags; this page streams
 * the steps in one-by-one (staggered reveal) like the real agent generating a
 * graph. The confidence gate streams in later, when the user asks to auto-accept
 * high-confidence invoices. Driven entirely by the demo store — no backend.
 * Delete with src/demo/ap.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  type Edge,
  type Node,
  type NodeProps,
  type NodeTypes,
  type ReactFlowInstance,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  CheckCircleIcon,
  RocketLaunchIcon,
  SparkleIcon,
} from '@phosphor-icons/react';
import { T } from 'gt-react';
import paths from '@/paths';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useToast } from '@/context/ToastContext';
import { StepNode } from '@/lib/steps/step-node';
import { useApWorkflowBuild } from '../provenance';
import {
  AP_WORKFLOW_DESCRIPTION,
  AP_WORKFLOW_NAME,
  buildApWorkflowGraph,
} from './ap-workflow';

// Wrap the real product node so each step can fade + scale in as it's revealed.
// All nodes stay mounted (so fitView frames the whole graph); only opacity
// changes, so the view never jumps mid-build.
function RevealableStepNode(props: NodeProps) {
  const revealed = (props.data as { __revealed?: boolean }).__revealed ?? true;
  return (
    <div
      className={cn(
        'transition-all duration-500 ease-out',
        revealed ? 'scale-100 opacity-100' : 'scale-90 opacity-0',
      )}
    >
      <StepNode {...props} />
    </div>
  );
}

const nodeTypes: NodeTypes = { stepNode: RevealableStepNode };

export function ApWorkflowBuildPage() {
  const build = useApWorkflowBuild();
  const built = build?.built ?? false;
  const confidenceAdded = build?.confidenceAdded ?? false;
  const { showToast } = useToast();
  const navigate = useNavigate();

  const { nodes, edges } = useMemo(
    () => buildApWorkflowGraph(confidenceAdded),
    [confidenceAdded],
  );

  const rfRef = useRef<ReactFlowInstance | null>(null);
  const [revealedIds, setRevealedIds] = useState<Set<string>>(
    () => new Set<string>(),
  );
  const [published, setPublished] = useState(false);

  // Reset the reveal animation when the build is cleared (demo reset / replay).
  useEffect(() => {
    if (!built) {
      setRevealedIds(new Set<string>());
      setPublished(false);
    }
  }, [built]);

  // Reveal the next not-yet-shown node on a timer. The set also grows when the
  // confidence gate is appended; matching by id keeps revealed nodes stable.
  useEffect(() => {
    if (!built) return;
    const next = nodes.find((n) => !revealedIds.has(n.id));
    if (!next) return;
    const delay = revealedIds.size === 0 ? 250 : 480;
    const timer = setTimeout(() => {
      setRevealedIds((prev) => new Set(prev).add(next.id));
    }, delay);
    return () => clearTimeout(timer);
  }, [built, nodes, revealedIds]);

  // Pan/zoom to frame the graph as nodes (and the appended gate) come in.
  useEffect(() => {
    if (!built) return;
    const timer = setTimeout(
      () => rfRef.current?.fitView({ padding: 0.2, duration: 400 }),
      120,
    );
    return () => clearTimeout(timer);
  }, [built, revealedIds]);

  const displayNodes = useMemo<Node[]>(
    () =>
      nodes.map((n) => ({
        ...n,
        data: { ...n.data, __revealed: revealedIds.has(n.id) },
      })),
    [nodes, revealedIds],
  );
  const displayEdges = useMemo<Edge[]>(
    () =>
      edges.map((e) => ({
        ...e,
        style: {
          ...e.style,
          opacity:
            revealedIds.has(e.source) && revealedIds.has(e.target) ? 1 : 0,
          transition: 'opacity 400ms ease',
        },
      })),
    [edges, revealedIds],
  );

  const buildComplete = built && revealedIds.size >= nodes.length;

  const handlePublish = () => {
    setPublished(true);
    showToast({
      color: 'success',
      title: 'Workflow published',
      message: `"${AP_WORKFLOW_NAME}" is now live.`,
    });
  };

  if (!built) {
    return (
      <div className="flex h-full min-h-[60vh] flex-col items-center justify-center text-center">
        <SparkleIcon weight="fill" className="size-10 text-violet-400" />
        <h2 className="mt-3 text-lg font-semibold">
          <T context="AP demo workflow build empty-state title">
            No workflow yet
          </T>
        </h2>
        <p className="mt-1 max-w-sm text-sm text-muted-foreground">
          <T context="AP demo workflow build empty-state body">
            Ask Nami to “make me an AP processing workflow” and it will build
            itself here.
          </T>
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100svh-8rem)] min-h-[32rem] flex-col gap-3">
      {/* Header banner */}
      <div className="flex items-center justify-between gap-3 rounded-lg border bg-card px-4 py-2.5">
        <div className="flex items-center gap-2.5">
          <SparkleIcon
            weight="fill"
            className="size-5 shrink-0 text-violet-500"
          />
          <div className="min-w-0">
            <p className="text-sm font-semibold leading-tight">
              {AP_WORKFLOW_NAME}
            </p>
            <p className="truncate text-xs leading-tight text-muted-foreground">
              {AP_WORKFLOW_DESCRIPTION}
            </p>
          </div>
        </div>
        {published ? (
          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-sm font-medium text-emerald-700">
            <CheckCircleIcon weight="fill" className="size-4" />
            <T context="AP demo workflow published label">Published</T>
          </span>
        ) : (
          <Button
            size="sm"
            className="shrink-0"
            disabled={!buildComplete}
            onClick={handlePublish}
          >
            <RocketLaunchIcon weight="fill" className="size-4" />
            <T context="button to publish the built AP workflow">
              Publish workflow
            </T>
          </Button>
        )}
      </div>

      {/* Canvas */}
      <div className="min-h-0 flex-1 overflow-hidden rounded-lg border">
        <ReactFlowProvider>
          <ReactFlow
            nodes={displayNodes}
            edges={displayEdges}
            nodeTypes={nodeTypes}
            onInit={(instance) => {
              rfRef.current = instance;
            }}
            fitView
            fitViewOptions={{ maxZoom: 1, padding: 0.2 }}
            proOptions={{ hideAttribution: true }}
            minZoom={0.2}
            maxZoom={2}
            nodesConnectable={false}
            nodesDraggable={false}
            elementsSelectable={false}
            edgesFocusable={false}
          >
            <Background gap={16} color="#e2e8f0" />
            <Controls showInteractive={false} />
          </ReactFlow>
        </ReactFlowProvider>
      </div>

      {/* Exit back to the real workflows list. */}
      <div className="flex justify-end">
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground"
          onClick={() =>
            void navigate({ to: paths.authenticated.workflows.list })
          }
        >
          <T context="link back to the workflows list from the AP demo build">
            Back to workflows
          </T>
        </Button>
      </div>
    </div>
  );
}
